import {useCallback, useEffect, useRef, useState} from 'react';
import type {ExamRequestFailure} from '@/utils/network/examRequest';
import i18n from '@/locales/init';
import style from './index.module.less';

export const STALE_SYNC_TIMEOUT_MS = 45_000;
export const SAVE_FAILURE_THRESHOLD = 2;

type Warning = 'offline' | 'connection' | 'unsynced';

interface Props {
  active: boolean;
  syncedAt: number;
  failureCount: number;
  lastFailure?: ExamRequestFailure;
  pending: boolean;
  onRetry: () => void;
}

const failureWarning = (failure?: ExamRequestFailure): Warning => {
  if (failure?.httpStatus) return 'unsynced';
  if (failure?.reason === 'timeout' || failure?.code === 'ERR_NETWORK' || failure?.code === 'NETWORK_ERR') {
    return 'connection';
  }
  return 'unsynced';
};

export default function DraftSyncNotice(props: Props) {
  const {active, syncedAt, failureCount, lastFailure, pending} = props;
  const latest = useRef(props);
  latest.current = props;
  const [warning, setWarning] = useState<Warning | null>(null);
  const warningRef = useRef<Warning | null>(null);
  const activatedAt = useRef(0);
  const lastSyncedAt = useRef(0);
  const onlineRecoveryAttempted = useRef(false);
  const observedFailure = useRef({count: failureCount, failure: lastFailure});

  const updateWarning = useCallback((next: Warning | null) => {
    warningRef.current = next;
    setWarning(next);
  }, []);

  useEffect(() => {
    onlineRecoveryAttempted.current = false;
    if (!active) {
      updateWarning(null);
      return;
    }

    activatedAt.current = Date.now();
    lastSyncedAt.current = latest.current.syncedAt;
    observedFailure.current = {count: latest.current.failureCount, failure: latest.current.lastFailure};
    updateWarning(!navigator.onLine
      ? 'offline'
      : latest.current.failureCount >= SAVE_FAILURE_THRESHOLD ? failureWarning(latest.current.lastFailure) : null);

    const offline = () => {
      onlineRecoveryAttempted.current = false;
      updateWarning('offline');
    };
    const online = () => {
      if (!warningRef.current) return;
      updateWarning(failureWarning(latest.current.lastFailure));
      if (latest.current.pending || onlineRecoveryAttempted.current) return;
      onlineRecoveryAttempted.current = true;
      latest.current.onRetry();
    };
    const timer = window.setInterval(() => {
      const baseline = Math.max(activatedAt.current, lastSyncedAt.current);
      if (!warningRef.current && Date.now() - baseline >= STALE_SYNC_TIMEOUT_MS) {
        updateWarning(failureWarning(latest.current.lastFailure));
      }
    }, 1000);

    window.addEventListener('offline', offline);
    window.addEventListener('online', online);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('offline', offline);
      window.removeEventListener('online', online);
    };
  }, [active, updateWarning]);

  useEffect(() => {
    if (!active) return;
    const hasNewFailure = failureCount > observedFailure.current.count
      || (failureCount > 0 && lastFailure !== observedFailure.current.failure);
    observedFailure.current = {count: failureCount, failure: lastFailure};
    if (syncedAt > lastSyncedAt.current) {
      lastSyncedAt.current = syncedAt;
      onlineRecoveryAttempted.current = false;
      updateWarning(null);
      return;
    }
    const nextWarning = failureWarning(lastFailure);
    const hasServerResponse = (lastFailure?.httpStatus || 0) > 0
      || (typeof lastFailure?.code === 'number' && Number.isFinite(lastFailure.code));
    if ((failureCount >= SAVE_FAILURE_THRESHOLD && warningRef.current !== 'offline')
      || (warningRef.current === 'offline' && hasNewFailure && hasServerResponse)) {
      updateWarning(nextWarning);
    }
  }, [active, syncedAt, failureCount, lastFailure, updateWarning]);

  if (!active || !warning) return null;

  return <div className={style.notice} role="alert">
    <span className={style.message}>{i18n.t(`common.dauyan_exam_sync_${warning}`)}</span>
    <button type="button" className={style.retry} disabled={pending} onClick={() => {
      if (!latest.current.pending) latest.current.onRetry();
    }}>{i18n.t(pending ? 'common.dauyan_exam_sync_retrying' : 'common.dauyan_exam_sync_retry')}</button>
  </div>;
}
